import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MediaLibraryPickerModal from './MediaLibraryPickerModal';

const formatApiError = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const messages = Object.values(payload.errors).find((entry) => Array.isArray(entry) && entry.length > 0);
    if (messages) {
      return messages.join(' ');
    }
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  return fallback;
};

const BioPagesManager = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState('');
  const [uploadingSlug, setUploadingSlug] = useState('');
  const [duplicatingSlug, setDuplicatingSlug] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [pickerSlug, setPickerSlug] = useState('');

  const publicBaseUrl = useMemo(() => {
    const configured = process.env.REACT_APP_PUBLIC_SITE_URL || process.env.REACT_APP_PUBLIC_HOMEPAGE_URL;
    if (configured) {
      return configured.replace(/\/$/, '');
    }

    return 'http://localhost:8000';
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.get('/admin/bios');
      setProfiles(Array.isArray(data.profiles) ? data.profiles : []);
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to load bio pages.'));
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const updateProfileField = (slug, field, value) => {
    setProfiles((previous) =>
      previous.map((profile) =>
        profile.slug === slug
          ? {
              ...profile,
              [field]: value,
            }
          : profile
      )
    );
  };

  const saveProfile = async (slug) => {
    const profile = profiles.find((entry) => entry.slug === slug);
    if (!profile) return;

    setSavingSlug(slug);
    setError('');
    setNotice('');
    try {
      const payload = {
        name: profile.name,
        title: profile.title,
        biography: profile.biography,
        thumbnail_url: profile.thumbnail_url,
        sort_order: Number(profile.sort_order || 0),
        is_active: Boolean(profile.is_active),
      };
      const { data } = await axios.put(`/admin/bios/${slug}`, payload);
      setNotice(data.message || `Saved ${profile.name}.`);
      if (data.profile) {
        setProfiles((previous) =>
          previous.map((entry) => (entry.slug === slug ? data.profile : entry))
        );
      }
    } catch (requestError) {
      setError(formatApiError(requestError, `Failed to save ${profile.name || 'bio'}.`));
    } finally {
      setSavingSlug('');
    }
  };

  const duplicateProfile = async (slug) => {
    setDuplicatingSlug(slug);
    setError('');
    setNotice('');

    try {
      const { data } = await axios.post(`/admin/bios/${slug}/duplicate`);
      const duplicatedProfile = data?.profile;

      if (duplicatedProfile?.slug) {
        setProfiles((previous) =>
          [...previous, duplicatedProfile].sort((left, right) => {
            const leftSort = Number(left?.sort_order ?? 0);
            const rightSort = Number(right?.sort_order ?? 0);
            if (leftSort !== rightSort) {
              return leftSort - rightSort;
            }
            return String(left?.name ?? '').localeCompare(String(right?.name ?? ''));
          })
        );
      } else {
        await loadProfiles();
      }

      setNotice(data?.message || 'Bio duplicated.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to duplicate bio.'));
    } finally {
      setDuplicatingSlug('');
    }
  };

  const uploadThumbnail = async (slug, file) => {
    if (!file) return;

    setUploadingSlug(slug);
    setError('');
    setNotice('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const { data } = await axios.post(`/admin/bios/${slug}/thumbnail`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateProfileField(slug, 'thumbnail_url', data.thumbnail_url || '');
      setNotice(data.message || 'Thumbnail uploaded.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to upload thumbnail.'));
    } finally {
      setUploadingSlug('');
    }
  };

  if (loading) {
    return <div className="card">Loading bio pages...</div>;
  }

  return (
    <section className="homepage-builder">
      <div className="card">
        <div className="homepage-builder__header">
          <div>
            <h2>Bio Pages</h2>
            <p className="text-muted">
              Manage each leader bio page and upload a thumbnail image for every person.
            </p>
          </div>
          <button className="btn btn-muted" type="button" onClick={loadProfiles}>
            Reload
          </button>
        </div>

        {notice && <div className="notice notice-success">{notice}</div>}
        {error && <div className="notice notice-danger">{error}</div>}
      </div>

      {profiles.map((profile) => (
        <article key={profile.slug} className="card homepage-section">
          <div className="homepage-builder__row">
            <h3>{profile.name}</h3>
            <a
              className="btn btn-muted"
              href={`${publicBaseUrl}/bios/${profile.slug}`}
              target="_blank"
              rel="noreferrer"
            >
              View Page
            </a>
          </div>

          <div className="bio-admin-grid">
            <div className="bio-admin-thumb-wrap">
              <img
                className="bio-admin-thumb"
                src={profile.thumbnail_url}
                alt={`${profile.name} thumbnail`}
              />
              <div className="field">
                <label>Upload Thumbnail</label>
                <input
                  className="input"
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    uploadThumbnail(profile.slug, file);
                    event.target.value = '';
                  }}
                />
                {uploadingSlug === profile.slug && <small className="text-muted">Uploading...</small>}
              </div>
            </div>

            <div className="bio-admin-fields">
              <div className="homepage-builder__grid" style={{ marginTop: 0 }}>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <label>Thumbnail URL</label>
                  <input
                    className="input"
                    value={profile.thumbnail_url || ''}
                    onChange={(event) => updateProfileField(profile.slug, 'thumbnail_url', event.target.value)}
                    placeholder="https://cdn.example.com/thumbnail.jpg"
                  />
                  <button
                    className="btn btn-muted"
                    type="button"
                    style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
                    onClick={() => setPickerSlug(profile.slug)}
                  >
                    Choose from Media Library
                  </button>
                </div>
                <div className="field">
                  <label>Name</label>
                  <input
                    className="input"
                    value={profile.name || ''}
                    onChange={(event) => updateProfileField(profile.slug, 'name', event.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Sort Order</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    value={profile.sort_order ?? 0}
                    onChange={(event) =>
                      updateProfileField(profile.slug, 'sort_order', Number(event.target.value))
                    }
                  />
                </div>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <label>Title / Role</label>
                  <input
                    className="input"
                    value={profile.title || ''}
                    onChange={(event) => updateProfileField(profile.slug, 'title', event.target.value)}
                  />
                </div>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <label>Biography</label>
                  <textarea
                    className="input"
                    rows={12}
                    value={profile.biography || ''}
                    onChange={(event) => updateProfileField(profile.slug, 'biography', event.target.value)}
                  />
                </div>
                <label
                  className="field"
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '0.6rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(profile.is_active)}
                    onChange={(event) => updateProfileField(profile.slug, 'is_active', event.target.checked)}
                  />
                  <span>Visible on public bios list</span>
                </label>
              </div>

              <div className="homepage-builder__row">
                <button
                  className="btn btn-muted"
                  type="button"
                  onClick={() => duplicateProfile(profile.slug)}
                  disabled={duplicatingSlug === profile.slug}
                >
                  {duplicatingSlug === profile.slug ? 'Duplicating...' : 'Duplicate Bio'}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => saveProfile(profile.slug)}
                  disabled={savingSlug === profile.slug}
                >
                  {savingSlug === profile.slug ? 'Saving...' : 'Save Bio'}
                </button>
              </div>
            </div>
          </div>
        </article>
      ))}

      <MediaLibraryPickerModal
        open={Boolean(pickerSlug)}
        onClose={() => setPickerSlug('')}
        onSelect={(url) => {
          if (!url || !pickerSlug) return;
          updateProfileField(pickerSlug, 'thumbnail_url', url);
          setPickerSlug('');
          setError('');
          setNotice('Thumbnail selected from media library.');
        }}
      />
    </section>
  );
};

export default BioPagesManager;
